import React, { useEffect, useState, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AnimatePresence, LazyMotion, domAnimation } from 'framer-motion';
import { Box } from '@mui/material';
import { ResumeCountProvider } from './contexts/ResumeCountContext';
import { GlobalStateProvider } from './contexts/GlobalStateContext';

// Components
import PageLoader from './components/PageLoader';
import Navbar from './components/Navbar';

// Pages (Lazy Loaded)
const Home = React.lazy(() => import('./pages/Home.js'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Jobs = React.lazy(() => import('./pages/Jobs'));
const Recruiters = React.lazy(() => import('./pages/Recruiters'));
const RecruitersDashboard = React.lazy(() => import('./pages/RecruitersDashboard'));
const Submissions = React.lazy(() => import('./pages/Submissions'));
const Login = React.lazy(() => import('./pages/Login'));
const Signup = React.lazy(() => import('./pages/Signup'));
const ResumeUpload = React.lazy(() => import('./pages/ResumeUpload'));
const ATSScore = React.lazy(() => import('./pages/ATSScore'));
const Search = React.lazy(() => import('./pages/Search'));
const ResumeProfile = React.lazy(() => import('./pages/ResumeProfile'));
const PublicJobApplication = React.lazy(() => import('./pages/PublicJobApplication'));
const Profiles = React.lazy(() => import('./pages/Profiles.js'));
const UserProfile = React.lazy(() => import('./pages/UserProfile'));


// Create premium theme
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#4F46E5', // Premium Indigo
      light: '#818CF8',
      dark: '#3730A3',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#8B5CF6', // Modern Violet
      light: '#A78BFA',
      dark: '#6D28D9',
    },
    background: {
      default: '#F8FAFC', // Slate 50
      paper: '#ffffff',
    },
    text: {
      primary: '#0F172A', // Slate 900
      secondary: '#64748B', // Slate 500
    },
    divider: 'rgba(226, 232, 240, 0.8)',
    success: {
      main: '#10B981',
      light: '#D1FAE5',
      dark: '#059669',
    },
    warning: {
      main: '#F59E0B',
      light: '#FEF3C7',
      dark: '#B45309',
    },
    error: {
      main: '#EF4444',
      light: '#FEE2E2',
      dark: '#B91C1C',
    },
  },
  shape: {
    borderRadius: 12,
  },
  typography: {
    fontFamily: "'Inter', sans-serif",
    h1: { fontWeight: 800, color: '#0F172A', letterSpacing: '-0.02em' },
    h2: { fontWeight: 700, color: '#0F172A', letterSpacing: '-0.02em' },
    h3: { fontWeight: 700, color: '#0F172A', letterSpacing: '-0.01em' },
    h4: { fontWeight: 700, color: '#0F172A', letterSpacing: '-0.01em' },
    h5: { fontWeight: 600, color: '#0F172A' },
    h6: { fontWeight: 600, color: '#0F172A' },
    subtitle1: { fontWeight: 500, color: '#475569' },
    subtitle2: { fontWeight: 500, color: '#64748B' },
    body1: { color: '#1E293B', lineHeight: 1.6 },
    body2: { color: '#64748B', lineHeight: 1.6 },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  shadows: [
    'none',
    '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
    '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
    '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
    '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
    ...Array(19).fill('none'),
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarColor: '#CBD5E1 transparent',
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            borderRadius: '8px',
            backgroundColor: '#CBD5E1',
          },
          '&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus': {
            backgroundColor: '#94A3B8',
          },
          '&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active': {
            backgroundColor: '#94A3B8',
          },
          '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#94A3B8',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          padding: '10px 20px',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:active': { transform: 'scale(0.98)' },
        },
        contained: {
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
          '&:hover': {
            boxShadow: '0 10px 15px -3px rgba(79, 70, 229, 0.2), 0 4px 6px -4px rgba(79, 70, 229, 0.1)',
            transform: 'translateY(-1px)',
          },
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': {
            borderWidth: '1.5px',
            backgroundColor: 'rgba(79, 70, 229, 0.04)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          border: '1px solid rgba(226, 232, 240, 0.8)',
          boxShadow: '0 4px 20px rgba(15, 23, 42, 0.03)',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 20px 25px -5px rgba(15, 23, 42, 0.05)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            backgroundColor: '#ffffff',
            '& fieldset': {
              borderColor: '#E2E8F0',
              borderWidth: '1.5px',
            },
            '&:hover fieldset': {
              borderColor: '#94A3B8',
            },
            '&.Mui-focused fieldset': {
              borderWidth: '2px',
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
        },
        colorPrimary: {
          backgroundColor: 'rgba(79, 70, 229, 0.1)',
          color: '#4F46E5',
          '&:hover': {
            backgroundColor: 'rgba(79, 70, 229, 0.2)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: '0 4px 20px rgba(15, 23, 42, 0.03)',
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          border: '1px solid rgba(226, 232, 240, 0.8)',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#F8FAFC',
            borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
          },
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid rgba(226, 232, 240, 0.4)',
          },
        },
      },
    },
  },
});

// Public Layout Component with Auth Check
const PublicLayout = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // If user is authenticated and trying to access public routes, redirect to dashboard
    if (isAuthenticated && ['/login', '/signup', '/'].includes(location.pathname)) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate, location]);

  return (
    <Box
      component="main"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        zIndex: 1,
      }}
    >
      <Suspense fallback={<PageLoader />}>
        {children}
      </Suspense>
    </Box>
  );
};

// App Layout Component
const AppLayout = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [open, setOpen] = useState(true);
  
  // Listen for drawer open/close events from Navbar
  useEffect(() => {
    const handleDrawerChange = (e) => {
      if (e.detail) {
        setOpen(e.detail.open);
      }
    };
    
    window.addEventListener('drawer-state-change', handleDrawerChange);
    return () => {
      window.removeEventListener('drawer-state-change', handleDrawerChange);
    };
  }, []);
  
  return (
    <Box sx={{ display: 'flex' }}>
      {isAuthenticated && <Navbar />}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          px: 4, // Professional padding
          pb: 4,
          pt: 0,
          minHeight: '100vh',
          backgroundColor: '#F8FAFC', // Consistent background
        }}
      >
        <Suspense fallback={<PageLoader />}>
          <AnimatePresence mode="wait">
            {children}
          </AnimatePresence>
        </Suspense>
      </Box>
    </Box>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Shared Job Layout Component
const SharedJobLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // If the path is not a valid share URL format, redirect to the application
    if (!location.pathname.startsWith('/share/')) {
      navigate('/share/invalid');
    }
  }, [location, navigate]);

  return (
    <Box
      component="main"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        zIndex: 1,
        backgroundColor: '#f4f7f9',
      }}
    >
      <Suspense fallback={<PageLoader />}>
        {children}
      </Suspense>
    </Box>
  );
};

// Main App Component
function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

// App Content Component
function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const isSharedJobPath = location.pathname.startsWith('/share/');
  const isRootPath = location.pathname === '/';

  // Redirect to error page if trying to access root from a shared job session
  useEffect(() => {
    const hasSharedJobHistory = sessionStorage.getItem('sharedJobAccess');
    if (hasSharedJobHistory && isRootPath) {
      navigate('/share/invalid');
    }
  }, [isRootPath, navigate]);

  // Set flag when accessing shared job
  useEffect(() => {
    if (isSharedJobPath) {
      sessionStorage.setItem('sharedJobAccess', 'true');
    }
  }, [isSharedJobPath]);

  return (
    <LazyMotion features={domAnimation}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {isSharedJobPath ? (
          // Render only the shared job application routes
          <Routes>
              <Route 
                path="/share/:shareableLink" 
                element={
                  <SharedJobLayout>
                    <PublicJobApplication />
                  </SharedJobLayout>
                } 
              />
              {/* Catch any invalid share URLs */}
              <Route 
                path="/share/*" 
                element={
                  <SharedJobLayout>
                    <PublicJobApplication />
                  </SharedJobLayout>
                } 
              />
            </Routes>
        ) : (
          // Render main application routes
          <AuthProvider>
            <GlobalStateProvider>
              <ResumeCountProvider>
                <Routes>
                  {/* Public Routes */}
                  <Route path="/login" element={
                    <PublicLayout>
                      <Login />
                    </PublicLayout>
                  } />
                  <Route path="/signup" element={
                    <PublicLayout>
                      <Signup />
                    </PublicLayout>
                  } />
                  <Route path="/" element={
                    <PublicLayout>
                      <Home />
                    </PublicLayout>
                  } />

                  {/* Protected Routes */}
                  <Route path="/dashboard" element={
                    <ProtectedRoute>
                      <AppLayout>
                        <Dashboard />
                      </AppLayout>
                    </ProtectedRoute>
                  } />
                  <Route path="/jobs" element={
                    <ProtectedRoute>
                      <AppLayout>
                        <Jobs />
                      </AppLayout>
                    </ProtectedRoute>
                  } />
                  <Route path="/recruiters" element={
                    <ProtectedRoute>
                      <AppLayout>
                        <Recruiters />
                      </AppLayout>
                    </ProtectedRoute>
                  } />
                  <Route path="/recruiters-dashboard" element={
                    <ProtectedRoute>
                      <AppLayout>
                        <RecruitersDashboard />
                      </AppLayout>
                    </ProtectedRoute>
                  } />
                  <Route path="/submissions" element={
                    <ProtectedRoute>
                      <AppLayout>
                        <Submissions />
                      </AppLayout>
                    </ProtectedRoute>
                  } />
                  <Route path="/resume-upload" element={
                    <ProtectedRoute>
                      <AppLayout>
                        <ResumeUpload />
                      </AppLayout>
                    </ProtectedRoute>
                  } />
                  <Route path="/ats-score" element={
                    <ProtectedRoute>
                      <AppLayout>
                        <ATSScore />
                      </AppLayout>
                    </ProtectedRoute>
                  } />
                  <Route path="/search" element={
                    <ProtectedRoute>
                      <AppLayout>
                        <Search />
                      </AppLayout>
                    </ProtectedRoute>
                  } />
                  <Route path="/resume-profile" element={
                    <ProtectedRoute>
                      <AppLayout>
                        <ResumeProfile />
                      </AppLayout>
                    </ProtectedRoute>
                  } />
                  <Route path="/user-profile" element={
                    <ProtectedRoute>
                      <AppLayout>
                        <UserProfile />
                      </AppLayout>
                    </ProtectedRoute>
                  } />
                  <Route path='/profiles' element={
                    <ProtectedRoute>
                      <AppLayout>
                        <Profiles />
                      </AppLayout>
                    </ProtectedRoute>
                  }
                  />

                  {/* Catch-all route - Redirect to dashboard if authenticated, otherwise to home */}
                  <Route path="*" element={
                    <Navigate to="/dashboard" replace />
                  } />
                </Routes>
              </ResumeCountProvider>
            </GlobalStateProvider>
          </AuthProvider>
        )}
      </ThemeProvider>
    </LazyMotion>
  );
}

export default App;